import type { FC } from "react";
import { useMemo } from "react";

import type { ISimpleDropdownItem } from "@similarweb/ui-components";
import { Dropdown, DropdownButton, SiteDropdownItem, Tooltip } from "@similarweb/ui-components";

import type { ISupportedLanguagesAndLocale } from "app/components/campaigns/campaigns-wizard/CampaignsWizardConsts";
import { LanguageDropdownItemWrapper } from "app/components/campaigns/searchEngine/CampaignsSearchEnginesStyledComponents";
import { getSupportedLanguagesForCountry } from "app/components/campaigns/searchEngine/inputFields/languageAndLocaleUtils/getSupportedLanguagesForCountry";
import type { ICampaignsLanguage } from "app/components/campaigns/searchEngine/SearchEnginesConsts";
import { getLocationNameToPresent } from "app/components/campaigns/shared/locationsAndLanguagesUtils";
import { translate } from "app/filters/i18nFilter";
import { reorderLanguages } from "../../shared/CampaignsUtils";
import { allTrackers } from "app/services/track/track";

const DROPDOWN_WIDTH = 191;

const LanguageDropdownItem: FC<ISimpleDropdownItem> = ({ children, onClick }) => (
    <LanguageDropdownItemWrapper
        title={children.toString()}
        onClick={onClick}
    >
        <SiteDropdownItem name={children as string} />
    </LanguageDropdownItemWrapper>
);

type Props = {
    selectedCountryName?: string;
    selectedLanguage?: ICampaignsLanguage;
    onSelect?: (language: ICampaignsLanguage) => void;
    languagesList: ISupportedLanguagesAndLocale[];
    isDisabled?: boolean;
    dropdownWidth?: number;
};

export const SearchEngineLanguagePicker = ({
    selectedCountryName,
    selectedLanguage,
    onSelect,
    languagesList = [],
    isDisabled = false,
    dropdownWidth = DROPDOWN_WIDTH,
}: Props) => {
    const availableLanguages = useMemo(() => {
        let langReturn: ICampaignsLanguage[] = [];
        if (selectedCountryName) {
            langReturn = getSupportedLanguagesForCountry(selectedCountryName, languagesList);
            langReturn = reorderLanguages(langReturn);
        }
        return langReturn;
    }, [selectedCountryName, languagesList]);

    const selectedLanguageIds = selectedLanguage ? { [selectedLanguage.id]: true } : {};

    const onSearchLanguage = (searchTerm: string) =>
        availableLanguages.filter((language) => language.text.includes(searchTerm));

    const toggleLanguage = (isOpen: boolean) => {
        if (!isOpen) {
            return;
        }
        allTrackers.dispatchTrackEvent("Drop Down", "open", "Language/open language");
    };

    const tooltipContent = translate(
        "seo.keywordsCampaign.wizard.searchEnginesStep.inputs.selectLanguage.disabledTooltip",
    );

    const searchPlaceHolder = translate(
        "seo.keywordsCampaign.wizard.searchEnginesStep.inputs.selectLanguage.dropdownPlaceholder",
    );

    const dropDownPlaceholder = translate(
        "seo.keywordsCampaign.wizard.searchEnginesStep.inputs.selectLanguage.placeholder",
    );

    const disabled = isDisabled || !selectedCountryName;

    return (
        <Tooltip
            content={tooltipContent}
            enabled={!selectedCountryName}
        >
            <div>
                <Dropdown
                    selectedIds={selectedLanguageIds}
                    onClick={onSelect}
                    disabled={disabled}
                    itemsComponent={LanguageDropdownItem}
                    dropdownPopupPlacement="ontop-left"
                    hasSearch={true}
                    debounce={250}
                    width={dropdownWidth}
                    buttonWidth={dropdownWidth}
                    onToggle={toggleLanguage}
                    onSearch={onSearchLanguage}
                    searchPlaceHolder={searchPlaceHolder}
                >
                    {/* Pass children as an array since Dropdown expects it */}
                    {[
                        <DropdownButton
                            key="languageDropdown_mostRecent"
                            dataAutomation={selectedLanguage ? selectedLanguage.id + "_selected" : undefined}
                            disabled={disabled}
                        >
                            {selectedLanguage ? getLocationNameToPresent(selectedLanguage.text) : dropDownPlaceholder}
                        </DropdownButton>,
                        ...availableLanguages,
                    ]}
                </Dropdown>
            </div>
        </Tooltip>
    );
};
